import { ProtocolError, type Direction, type PhysicalChannel } from '@spectrabus/protocol';

// Control-plane payloads carried inside SBFA envelopes alongside SBUS data.
// Every payload is self-describing through a leading four-character magic so
// a receiver can dispatch after authentication without out-of-band context.
const MANIFEST_SEGMENT_MAGIC = 0x53424d46; // 'SBMF'
const RANK_FEEDBACK_MAGIC = 0x53425246; // 'SBRF'
const CONTROL_VERSION = 1;

export const MANIFEST_SEGMENT_HEADER_BYTES = 16;
export const MANIFEST_SEGMENT_PAYLOAD_BYTES = 704;
export const RANK_FEEDBACK_HEADER_BYTES = 12;
export const MAX_FEEDBACK_GENERATIONS = 4_096;

export interface ManifestSegment {
  readonly segmentIndex: number;
  readonly segmentCount: number;
  readonly manifestTotalBytes: number;
  readonly bytes: Uint8Array;
}

export interface RankFeedback {
  /** True once the receiver holds the complete, commitment-bound manifest. */
  readonly manifestReceived: boolean;
  /** True once the receiver has verified both forward key confirmations. */
  readonly transferAuthorised: boolean;
  /** True once the whole object opened and hashed correctly. */
  readonly objectVerified: boolean;
  /**
   * True when this receiver does not listen to the forward audio band (for
   * example a speaker-priority phone); the sender should keep its speaker
   * quiet rather than emit into a deaf channel.
   */
  readonly forwardAudioDisabled: boolean;
  /** Remaining rank deficit per generation, capped at 255 apiece. */
  readonly rankDeficits: Uint8Array;
  /** SHA-256 of the recovered plaintext, present only with objectVerified. */
  readonly receiverObjectHash: string | null;
}

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

export function encodeManifestSegment(segment: ManifestSegment): Uint8Array {
  if (
    !Number.isInteger(segment.segmentIndex) ||
    !Number.isInteger(segment.segmentCount) ||
    segment.segmentCount < 1 ||
    segment.segmentIndex < 0 ||
    segment.segmentIndex >= segment.segmentCount ||
    segment.bytes.length < 1 ||
    segment.bytes.length > MANIFEST_SEGMENT_PAYLOAD_BYTES ||
    !Number.isInteger(segment.manifestTotalBytes) ||
    segment.manifestTotalBytes < 1
  ) {
    throw new ProtocolError('CONTROL_SEGMENT_BOUNDS', 'Manifest segment fields are outside bounds.');
  }
  const encoded = new Uint8Array(MANIFEST_SEGMENT_HEADER_BYTES + segment.bytes.length);
  const writer = view(encoded);
  writer.setUint32(0, MANIFEST_SEGMENT_MAGIC);
  writer.setUint8(4, CONTROL_VERSION);
  writer.setUint8(5, 0);
  writer.setUint16(6, segment.segmentIndex);
  writer.setUint16(8, segment.segmentCount);
  writer.setUint16(10, segment.bytes.length);
  writer.setUint32(12, segment.manifestTotalBytes);
  encoded.set(segment.bytes, MANIFEST_SEGMENT_HEADER_BYTES);
  return encoded;
}

export function decodeManifestSegment(bytes: Uint8Array): ManifestSegment {
  if (bytes.length < MANIFEST_SEGMENT_HEADER_BYTES + 1) {
    throw new ProtocolError('CONTROL_SEGMENT_TRUNCATED', 'Manifest segment is shorter than its header.');
  }
  const reader = view(bytes);
  if (reader.getUint32(0) !== MANIFEST_SEGMENT_MAGIC || reader.getUint8(4) !== CONTROL_VERSION) {
    throw new ProtocolError('CONTROL_SEGMENT_HEADER', 'Manifest segment magic or version mismatch.');
  }
  const segmentIndex = reader.getUint16(6);
  const segmentCount = reader.getUint16(8);
  const segmentBytes = reader.getUint16(10);
  const manifestTotalBytes = reader.getUint32(12);
  if (
    segmentCount < 1 ||
    segmentIndex >= segmentCount ||
    segmentBytes < 1 ||
    bytes.length !== MANIFEST_SEGMENT_HEADER_BYTES + segmentBytes
  ) {
    throw new ProtocolError('CONTROL_SEGMENT_BOUNDS', 'Manifest segment length fields are inconsistent.');
  }
  return {
    segmentIndex,
    segmentCount,
    manifestTotalBytes,
    bytes: bytes.slice(MANIFEST_SEGMENT_HEADER_BYTES),
  };
}

export function segmentManifest(manifestBytes: Uint8Array): readonly ManifestSegment[] {
  if (manifestBytes.length < 1) {
    throw new ProtocolError('CONTROL_SEGMENT_BOUNDS', 'A manifest must contain at least one byte.');
  }
  const segmentCount = Math.ceil(manifestBytes.length / MANIFEST_SEGMENT_PAYLOAD_BYTES);
  return Array.from({ length: segmentCount }, (_, segmentIndex) => ({
    segmentIndex,
    segmentCount,
    manifestTotalBytes: manifestBytes.length,
    bytes: manifestBytes.slice(
      segmentIndex * MANIFEST_SEGMENT_PAYLOAD_BYTES,
      Math.min(manifestBytes.length, (segmentIndex + 1) * MANIFEST_SEGMENT_PAYLOAD_BYTES),
    ),
  }));
}

/**
 * Reassembles manifest segments as they arrive. Integrity of the assembled
 * bytes is not decided here: the caller verifies the manifest commitment and
 * the discovery binding through the crypto layer before trusting anything.
 */
export class ManifestAssembler {
  #segments = new Map<number, Uint8Array>();
  #segmentCount: number | null = null;
  #totalBytes: number | null = null;

  public get complete(): boolean {
    return this.#segmentCount !== null && this.#segments.size === this.#segmentCount;
  }

  public add(segment: ManifestSegment): boolean {
    if (this.#segmentCount === null) {
      if (segment.segmentCount > 32_768) {
        throw new ProtocolError('CONTROL_SEGMENT_BOUNDS', 'Manifest segment count exceeds the receiver bound.');
      }
      this.#segmentCount = segment.segmentCount;
      this.#totalBytes = segment.manifestTotalBytes;
    }
    if (
      segment.segmentCount !== this.#segmentCount ||
      segment.manifestTotalBytes !== this.#totalBytes
    ) {
      throw new ProtocolError('CONTROL_SEGMENT_MISMATCH', 'Manifest segment metadata changed mid-assembly.');
    }
    if (this.#segments.has(segment.segmentIndex)) return false;
    this.#segments.set(segment.segmentIndex, segment.bytes);
    return true;
  }

  public takeManifestBytes(): Uint8Array | null {
    if (!this.complete || this.#segmentCount === null || this.#totalBytes === null) return null;
    const assembled = new Uint8Array(this.#totalBytes);
    let offset = 0;
    for (let index = 0; index < this.#segmentCount; index += 1) {
      const segment = this.#segments.get(index);
      if (segment === undefined || offset + segment.length > this.#totalBytes) {
        throw new ProtocolError('CONTROL_SEGMENT_MISMATCH', 'Manifest reassembly bounds are inconsistent.');
      }
      assembled.set(segment, offset);
      offset += segment.length;
    }
    if (offset !== this.#totalBytes) {
      throw new ProtocolError('CONTROL_SEGMENT_MISMATCH', 'Reassembled manifest length mismatch.');
    }
    return assembled;
  }

  public reset(): void {
    this.#segments.clear();
    this.#segmentCount = null;
    this.#totalBytes = null;
  }
}

const FEEDBACK_FLAG_OBJECT_VERIFIED = 0b0000_0001;
const FEEDBACK_FLAG_MANIFEST_RECEIVED = 0b0000_0010;
const FEEDBACK_FLAG_TRANSFER_AUTHORISED = 0b0000_0100;
const FEEDBACK_FLAG_FORWARD_AUDIO_DISABLED = 0b0000_1000;

export function encodeRankFeedback(feedback: RankFeedback): Uint8Array {
  if (feedback.rankDeficits.length > MAX_FEEDBACK_GENERATIONS) {
    throw new ProtocolError('CONTROL_FEEDBACK_BOUNDS', 'Rank feedback exceeds the generation bound.');
  }
  if (feedback.objectVerified !== (feedback.receiverObjectHash !== null)) {
    throw new ProtocolError(
      'CONTROL_FEEDBACK_BOUNDS',
      'A verification claim must carry the receiver object hash, and only then.',
    );
  }
  const hashBytes = feedback.receiverObjectHash === null ? 0 : 32;
  const encoded = new Uint8Array(RANK_FEEDBACK_HEADER_BYTES + feedback.rankDeficits.length + hashBytes);
  const writer = view(encoded);
  writer.setUint32(0, RANK_FEEDBACK_MAGIC);
  writer.setUint8(4, CONTROL_VERSION);
  writer.setUint8(
    5,
    (feedback.objectVerified ? FEEDBACK_FLAG_OBJECT_VERIFIED : 0) |
      (feedback.manifestReceived ? FEEDBACK_FLAG_MANIFEST_RECEIVED : 0) |
      (feedback.transferAuthorised ? FEEDBACK_FLAG_TRANSFER_AUTHORISED : 0) |
      (feedback.forwardAudioDisabled ? FEEDBACK_FLAG_FORWARD_AUDIO_DISABLED : 0),
  );
  writer.setUint16(6, feedback.rankDeficits.length);
  writer.setUint32(8, 0);
  encoded.set(feedback.rankDeficits, RANK_FEEDBACK_HEADER_BYTES);
  if (feedback.receiverObjectHash !== null) {
    if (!/^[\da-f]{64}$/u.test(feedback.receiverObjectHash)) {
      throw new ProtocolError('CONTROL_FEEDBACK_BOUNDS', 'Receiver object hash is non-canonical.');
    }
    const hash = feedback.receiverObjectHash;
    for (let index = 0; index < 32; index += 1) {
      encoded[RANK_FEEDBACK_HEADER_BYTES + feedback.rankDeficits.length + index] = Number.parseInt(
        hash.slice(index * 2, index * 2 + 2),
        16,
      );
    }
  }
  return encoded;
}

export function decodeRankFeedback(bytes: Uint8Array): RankFeedback {
  if (bytes.length < RANK_FEEDBACK_HEADER_BYTES) {
    throw new ProtocolError('CONTROL_FEEDBACK_TRUNCATED', 'Rank feedback is shorter than its header.');
  }
  const reader = view(bytes);
  if (reader.getUint32(0) !== RANK_FEEDBACK_MAGIC || reader.getUint8(4) !== CONTROL_VERSION) {
    throw new ProtocolError('CONTROL_FEEDBACK_HEADER', 'Rank feedback magic or version mismatch.');
  }
  const flags = reader.getUint8(5);
  const generationCount = reader.getUint16(6);
  if (reader.getUint32(8) !== 0) {
    throw new ProtocolError('CONTROL_FEEDBACK_HEADER', 'Rank feedback reserved bytes must be zero.');
  }
  const objectVerified = (flags & FEEDBACK_FLAG_OBJECT_VERIFIED) !== 0;
  const hashBytes = objectVerified ? 32 : 0;
  if (bytes.length !== RANK_FEEDBACK_HEADER_BYTES + generationCount + hashBytes) {
    throw new ProtocolError('CONTROL_FEEDBACK_BOUNDS', 'Rank feedback length fields are inconsistent.');
  }
  let receiverObjectHash: string | null = null;
  if (objectVerified) {
    receiverObjectHash = Array.from(
      bytes.slice(RANK_FEEDBACK_HEADER_BYTES + generationCount),
      (value) => value.toString(16).padStart(2, '0'),
    ).join('');
  }
  return {
    manifestReceived: (flags & FEEDBACK_FLAG_MANIFEST_RECEIVED) !== 0,
    transferAuthorised: (flags & FEEDBACK_FLAG_TRANSFER_AUTHORISED) !== 0,
    objectVerified,
    forwardAudioDisabled: (flags & FEEDBACK_FLAG_FORWARD_AUDIO_DISABLED) !== 0,
    rankDeficits: bytes.slice(RANK_FEEDBACK_HEADER_BYTES, RANK_FEEDBACK_HEADER_BYTES + generationCount),
    receiverObjectHash,
  };
}

export type ControlPayloadKind = 'transport' | 'manifest-segment' | 'rank-feedback' | 'unknown';

/** Dispatches an authenticated SBFA payload on its self-describing magic. */
export function classifyControlPayload(payload: Uint8Array): ControlPayloadKind {
  if (payload.length < 4) return 'unknown';
  const magic = view(payload).getUint32(0);
  if (magic === 0x53425553) return 'transport';
  if (magic === MANIFEST_SEGMENT_MAGIC) return 'manifest-segment';
  if (magic === RANK_FEEDBACK_MAGIC) return 'rank-feedback';
  return 'unknown';
}

export interface PeekedFrameHeader {
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly sequence: number;
  readonly symbolId: number;
  readonly profileId: string;
  readonly contextId: string;
}

const PEEK_DIRECTIONS: Readonly<Record<number, Direction>> = {
  1: 'sender-to-receiver',
  2: 'receiver-to-sender',
};
const PEEK_CHANNELS: Readonly<Record<number, PhysicalChannel>> = {
  1: 'optical',
  2: 'acoustic',
};

/**
 * Reads the clear-text SBFA header without verifying anything. This exists
 * only so a receiver can construct the right authenticated expectation for
 * openPhysicalFrameEnvelope; a forged header simply makes that authoritative
 * open fail. Returns null instead of throwing on malformed input because a
 * physical channel delivers arbitrary bytes as a matter of course.
 */
export function peekPhysicalFrameHeader(envelope: Uint8Array): PeekedFrameHeader | null {
  if (envelope.length < 40) return null;
  const reader = view(envelope);
  if (reader.getUint32(0) !== 0x53424641 || reader.getUint8(4) !== 1) return null;
  const direction = PEEK_DIRECTIONS[reader.getUint8(5)];
  const channel = PEEK_CHANNELS[reader.getUint8(6)];
  if (direction === undefined || channel === undefined) return null;
  const profileLength = reader.getUint8(7);
  const contextLength = reader.getUint8(8);
  if (profileLength < 1 || contextLength < 1 || envelope.length < 40 + profileLength + contextLength) {
    return null;
  }
  try {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    return {
      direction,
      channel,
      sequence: reader.getUint32(28),
      symbolId: reader.getUint32(32),
      profileId: decoder.decode(envelope.slice(40, 40 + profileLength)),
      contextId: decoder.decode(envelope.slice(40 + profileLength, 40 + profileLength + contextLength)),
    };
  } catch {
    return null;
  }
}

/** Reads the leading four-character magic of a raw payload, if present. */
export function payloadMagic(bytes: Uint8Array): number | null {
  if (bytes.length < 4) return null;
  return view(bytes).getUint32(0);
}
